import { useEffect } from "react";
import styled, { ThemeProvider } from "styled-components";
import I18n from "../../../translations/i18n";

import theme, { ThemeEditableConfigTypes } from "../theme";
import { Property } from "../../types";
import MobileBreakpoints from "../../../utilities/MobileBreakpoints";
import Airbnb from "./Airbnb";
import Arrow from "./Arrow";
import MovingDots from "./MovingDots";
import { getQueryStringValue } from "../../../utilities/queryStringHelpers";

const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0 30px;
  * {
    font-family: ${({ theme }) => theme.customThemeOptions.fontFamily};
    box-sizing: border-box;
    color: ${({ theme }) => theme.customThemeOptions.colors.body};
  }
`;

const LogoPropertyRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.laptop`
    flex-direction: row;
  `}
`;

const PartnerLogo = styled.img`
  max-width: 150px;
  margin: 0 0 25px;
  ${({ theme }) => theme.tablet`
    max-width: 250px;
    margin: 0 0 34px;
  `}
  ${({ theme }) => theme.laptop`
    margin: 0 53px 0 0;
  `}
`;

const StyledArrow = styled(Arrow)`
  width: 56.88px;
  height: 43.4px;
  ${({ theme }) => theme.laptop`
    transform: rotate(-90deg);
  `}
`;

const IconContainer = styled.div`
  width: min-content;
  display: inline-block;
  position: relative;
  top: 4px;
  right: 8px;
  svg {
    width: 26px;
  }
`;

const HotelInfo = styled.div`
  margin: 25px 0 0;
  display: flex;
  flex-direction: column;
  text-align: center;
  ${({ theme }) => theme.tablet`
    margin: 33px 0 0;  
  `}
  ${({ theme }) => theme.laptop`
    margin: 0 0 0 46px; 
    text-align: left; 
  `}
`;

const HotelSiteMsg = styled.div`
  font-weight: 600;
  color: #666666 !important;
  ${({ theme }) => theme.tablet`
    font-size: 22px;
  `}
`;

const HotelName = styled(HotelSiteMsg)`
  color: ${({ theme }) => theme.customThemeOptions.colors.charcoal} !important;
  font-size: 22px;
  margin: 0 0 11px;
  ${({ theme }) => theme.tablet`
    margin: 0 0 18px;
    font-size: 26px;
  `}
`;

const BeingDirectedMsg = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #666666 !important;
  margin: 53px 0 79px;
  text-align: center;
  ${({ theme }) => theme.tablet`
    margin: 80px 0;
  `}
  ${({ theme }) => theme.laptop`
    margin: 69px 0 66px;
  `}
`;

const BookDirectInterstitial = ({ themeConfigs, property, partner }: Props) => {
  let pageType = null,
    siteMessage = null,
    redirectMessage = null;

  let redirectUrl = getQueryStringValue("redirect_url");
  const newExtensionDownload = getQueryStringValue("gb_new_download");
  const strategicListing = getQueryStringValue("gb_strategic_listing");

  if (redirectUrl?.includes("airbnb")) {
    pageType = "airbnb";
    siteMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.hotel_site_message_third_party",
      { site: "Airbnb" }
    );
    redirectMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.redirect_message_third_party",
      { site: "Airbnb" }
    );
  } else if (redirectUrl?.includes("priceline")) {
    pageType = "priceline";
    siteMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.hotel_site_message_third_party",
      { site: "Priceline" }
    );
    redirectMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.redirect_message_third_party",
      { site: "Priceline" }
    );
  } else if (redirectUrl?.includes("agoda")) {
    pageType = "agoda";
    siteMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.hotel_site_message_third_party",
      { site: "Agoda" }
    );
    redirectMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.redirect_message_third_party",
      { site: "Agoda" }
    );
  } else if (
    redirectUrl?.includes("booking.com") &&
    !redirectUrl?.includes("secure-hotel-booking") &&
    !redirectUrl?.includes("verticalbooking")
  ) {
    pageType = "booking.com";
    siteMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.hotel_site_message_third_party",
      { site: "Booking.com" }
    );
    redirectMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.redirect_message_third_party",
      { site: "Booking.com" }
    );
  } else {
    pageType = "official";
    siteMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.hotel_site_message_official"
    );
    redirectMessage = I18n.t(
      "views.dmo_partners.pages.book_direct_interstitial.redirect_message_official"
    );
  }

  useEffect(() => {
    const interval = setTimeout(() => {
      if (redirectUrl && typeof redirectUrl === "string") {
        try {
          const hotelUrl = new URL(redirectUrl);
          if (newExtensionDownload) hotelUrl.searchParams.set("gb_new_download", "true");
          if (strategicListing) hotelUrl.searchParams.set("gb_strategic_listing", "true");

          window.location.replace(hotelUrl.href);
        } catch {
          window.location.replace(redirectUrl);
        }
      }
    }, 3000);
    return () => {
      clearInterval(interval);
    };
  });

  return (
    <ThemeProvider
      theme={{
        ...theme(themeConfigs),
        ...MobileBreakpoints,
      }}
    >
      <Container>
        <LogoPropertyRow>
          <PartnerLogo src={partner?.logo ?? ''} />
          <StyledArrow
            fill={themeConfigs.colors.primary}
            stroke={themeConfigs.colors.primary}
          />
          <HotelInfo>
            <HotelName>{property.name}</HotelName>
            <HotelSiteMsg>
              {pageType === "airbnb" ? (
                <IconContainer>
                  <Airbnb fill={themeConfigs.colors.primary} />
                </IconContainer>
              ) : null}
              {siteMessage}
            </HotelSiteMsg>
          </HotelInfo>
        </LogoPropertyRow>
        <BeingDirectedMsg>{redirectMessage}</BeingDirectedMsg>
        <MovingDots duration={1000} numberOfDots={6} />
      </Container>
    </ThemeProvider>
  );
};

interface Props {
  themeConfigs: ThemeEditableConfigTypes;
  property: Property;
  partner: Partner;
}

interface Partner {
  logo: string;
}

export default BookDirectInterstitial;
